import { z } from 'zod'
import { IncidentLocationSchema, type IncidentLocation } from '@/entities/incident/api/types'

export type LiveGuardStatus = 'OnShift' | 'Idle' | 'NoSignal'

/**
 * A recorded ping, as the history and playback endpoints return it. Deliberately narrower than
 * LiveGuardResponse: a stored ping carries no status, because status is a live judgement about
 * how long ago the last ping arrived and means nothing applied to a point in a finished trail.
 */
export interface GuardLocationPing {
  guardUserId: number
  latitude: number | null
  longitude: number | null
  accuracyMeters: number | null
  isWithinSite: boolean
  recordedAt: string | null
}

export interface LiveGuardResponse {
  guardUserId: number
  siteId: number
  shiftId: number | null
  status: LiveGuardStatus
  guardName: string
  latitude: number | null
  longitude: number | null
  accuracyMeters: number | null
  isWithinSite: boolean
  recordedAt: string | null
  batteryPercentage: number | null
  photoUrl: string | null
}

export const liveGuardSchema = z.object({
  guardUserId: z.number(),
  siteId: z.number(),
  shiftId: z.number().nullish(),
  status: z.enum(['OnShift', 'Idle', 'NoSignal']).catch('NoSignal'),
  guardName: z.string().nullish(),
  latitude: z.number().nullish(),
  longitude: z.number().nullish(),
  accuracyMeters: z.number().nullish(),
  isWithinSite: z.boolean().nullish(),
  recordedAt: z.string().nullish(),
  batteryPercentage: z.number().nullish(),
  photoUrl: z.string().nullish(),
})

export const liveGuardsResponseSchema = z.object({
  guards: z.array(liveGuardSchema),
  positionsDegraded: z.boolean().nullish(),
  incidents: z.array(IncidentLocationSchema).nullish(),
  incidentsTruncated: z.boolean().nullish(),
})

export interface LiveGuardsResponse {
  guards: LiveGuardResponse[]
  positionsDegraded: boolean
  incidents?: IncidentLocation[]
  incidentsTruncated?: boolean
}

